import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import activityPanelStore from "shared/store/activityPanelStore";
import { ConstraintDocument, ConstraintMode } from "types/ConstraintProfileTypes";
import { fillMissingGroups } from "utils/constraintDocument";
import {
  buildVincoliRecap,
  RecapBullet,
} from "pages/assistant/components/activity-panel/panels/constraints/lifecycle/buildVincoliRecap";
import styles from "./finecoConstraintsSummaryArtifact.module.css";

export type ConstraintsSummaryData = {
  mode: ConstraintMode;
  constraints: ConstraintDocument;
  baseline: ConstraintDocument;
  applied_profile: { id: string; name: string } | null;
};

// A profile saved before the wrapper group existed (schema version 1) has no
// `wrapper`; the panel fills it from the predefiniti when the profile is
// applied, so the working document carries one. Fill the card's baseline the
// same way, else the wrapper share bounds never show.
const WRAPPER_DEFAULTS: ConstraintDocument = {
  wrapper: { wrapper_percent_min: 0, wrapper_percent_max: 1 },
};

type Props = {
  data: ConstraintsSummaryData;
};

const Bullets = ({ bullets }: { bullets: RecapBullet[] }) => (
  <ul className={styles.list}>
    {bullets.map((b, i) => (
      <li key={`${b.label}-${i}`}>
        {b.value ? `${b.label}: ${b.value}` : b.label}
        {b.children && <Bullets bullets={b.children} />}
      </li>
    ))}
  </ul>
);

/**
 * «Vincoli impostati» card, returned by `fineco_show_constraints`. Same recap
 * as Invia (only what differs from the applied profile or the Vincoli
 * Predefiniti), built from the document + baseline the tool snapshotted, so
 * it does not depend on the live panel state. Names come from the activity
 * panel stores, loaded here if the panel has not loaded them yet; until then
 * the recap falls back to the ISIN / id, as the panel does.
 */
const FinecoConstraintsSummaryArtifact = ({ data }: Props) => {
  const { t } = useTranslation();
  const productLists = activityPanelStore((s) => s.productLists);
  const microcategoryNames = activityPanelStore((s) => s.microcategoryNames);
  const productDisplayCache = activityPanelStore((s) => s.productDisplayCache);

  const product = data.constraints.product as Record<string, any> | null | undefined;

  useEffect(() => {
    const store = activityPanelStore.getState();
    if (store.productLists.length === 0) store.fetchProductLists();
    if (Object.keys(store.microcategoryNames).length === 0) store.fetchMicrocategoryNames();
    store.fetchProductDisplayData([
      ...Object.keys(product?.individual_products ?? {}).map((isin) => ({ isin })),
      ...Object.keys(product?.list_products ?? {}).map((isin) => ({ isin })),
      ...Object.entries(product?.portfolio_products ?? {}).map(([isin, p]: [string, any]) => ({
        isin,
        investment_type: p?.investment_type ?? null,
      })),
    ]);
  }, [product]);

  const recap = useMemo(
    () =>
      buildVincoliRecap(
        data.constraints,
        fillMissingGroups(data.baseline, WRAPPER_DEFAULTS),
        data.mode,
        data.applied_profile ? { ...data.applied_profile, document: data.baseline } : null,
        {
          t,
          getProductDisplay: (isin) => productDisplayCache[isin],
          productLists,
          microcategoryNames,
          importedPortfolioProducts: null,
        },
      ),
    [data, t, productDisplayCache, productLists, microcategoryNames],
  );

  return (
    <div className={styles.card}>
      <div className={styles.title}>{t("constraintsSummary.title")}</div>
      {data.applied_profile && (
        <div className={styles.configuration}>
          {`${t("constraints.configurationLineLabel")}: ${recap.configurationName}`}
        </div>
      )}
      {recap.sections.length === 0 ? (
        <p className={styles.empty}>
          {data.applied_profile ? t("constraintsSummary.allProfile") : t("constraintsSummary.allDefault")}
        </p>
      ) : (
        recap.sections.map((s) => (
          <div key={s.heading} className={styles.section}>
            <div className={styles.heading}>{s.heading}</div>
            <Bullets bullets={s.bullets} />
          </div>
        ))
      )}
    </div>
  );
};

export default FinecoConstraintsSummaryArtifact;
